import React, { useEffect, useState } from 'react';
import { CompactRow } from '@grnhse/seedling/lib/birch/layouts/compact_row';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import {
  RichTextField,
  SimpleFormMultiDropdown,
  SimpleFormSingleDropdown,
  SimpleFormTextField,
  PaginatedMultiDropdown,
  StackWithHiddenComponents,
} from '../../shared/form/Fields';
import {
  RejectionEmailOptionsResponse,
  TemplateOption,
  EmailTemplateResponse,
  TemplateAttachment,
  BirchDatePickerFormat,
} from '../../shared/types';
import SendLaterRow from '../email/send_later_row';

import { withLocalePrefix } from '../../../shared/utils/translation';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import OfficeAndTemplateFields from '../email/office_and_template_fields';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { standardRequestWithErrorFlash } from '../../../shared/utils/request';
import DomainWarningCallout from 'candidate_profile_redesign/shared/form/EmailDomainWarningCallout';
import CheckboxField from '@grnhse/seedling/lib/birch/components/forms/fields/checkbox_field';
import { Body, Label } from '@grnhse/seedling/lib/birch/typography';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';

const t = withLocalePrefix('candidate_profile_redesign.modals');

const RejectionEmailFields = ({
  templateOptions,
  fromOptions,
  toOptions,
  officeOptionsPath,
  sendAt,
  numOfCandidatesToReject,
  bulkMode,
  canSendEmail,
  getCcOptionsPath,
  shouldDisplayEmailDomainWarning,
  dateFormatBirchPicker,
}: RejectionEmailOptionsResponse & {
  bulkMode: boolean;
  canSendEmail?: boolean;
  getCcOptionsPath: string;
  shouldDisplayEmailDomainWarning: boolean;
  dateFormatBirchPicker: BirchDatePickerFormat;
}) => {
  const { watch, setValue } = useFormContext();

  const template = watch('template');
  const sendEmail = watch('send_email');

  const [templateLoading, setTemplateLoading] = useState(false);
  const [attachmentOptions, setAttachmentOptions] = useState<Item[]>([]);

  const transformTemplateAttachments = (attachments: TemplateAttachment[]) => {
    return attachments.map((a: TemplateAttachment) => ({
      label: a.filename,
      value: a.url,
    }));
  };

  const handleTemplateChange = async (newTemplate: TemplateOption) => {
    try {
      setTemplateLoading(true);
      const {
        data: {
          from,
          subject,
          html_body,
          cc,
          attachment,
          cc_coordinator,
          cc_recruiter,
        },
      } = await standardRequestWithErrorFlash.get<EmailTemplateResponse>(
        newTemplate.value
      );
      //fromOptions includes extra data we need (domain, verified status), so lookup value there
      const fromValue = fromOptions.find((option) => option.value === from);
      const attachments = transformTemplateAttachments(attachment);
      setValue('from', fromValue);
      setValue(
        'cc',
        cc.map((email: string) => ({ label: email, value: email }))
      );
      setValue('subject', subject);
      setValue('html_body', html_body);
      setAttachmentOptions(attachments);
      setValue('default_attachments', attachments);
      setValue('cc_coordinator', cc_coordinator);
      setValue('cc_recruiter', cc_recruiter);
    } finally {
      setTemplateLoading(false);
    }
  };

  useEffect(() => {
    if (!template) {
      return;
    }
    handleTemplateChange(template).then(() => null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);

  if (!canSendEmail) {
    return null;
  }

  return (
    <StackWithHiddenComponents>
      {shouldDisplayEmailDomainWarning && <DomainWarningCallout compact />}
      <OfficeAndTemplateFields
        officeOptionsPath={officeOptionsPath}
        templateOptions={templateOptions}
        showOfficeSpecificTemplateDropdown={!!officeOptionsPath}
      />
      {bulkMode && (
        <InformationalCallOut
          bodyText={
            t('reject.bulk.no_application_tokens') +
            ' {{START_DATE}}, {{APPLIED_DATE}}, {{INTERVIEW_SCHEDULE}}, {{INTERVIEW_SCHEDULE_START_TIME}}'
          }
        />
      )}
      <SimpleFormSingleDropdown
        items={fromOptions}
        name={`from`}
        label={t('email.from')}
        rules={{ required: sendEmail && t('email.from_required') }}
        clearable={false}
        disabled={templateLoading}
        isPiiBlurred
      />
      {!bulkMode ? (
        <SimpleFormSingleDropdown
          items={toOptions}
          name={`to`}
          label={t('email.to')}
          rules={{ required: sendEmail && t('email.to_required') }}
          clearable={false}
          disabled={templateLoading}
          isPiiBlurred
        />
      ) : (
        <Stack spacing={1}>
          <Label>{t('email.to')}</Label>
          <CompactRow>
            <Body>
              {t('reject.bulk.to', { count: numOfCandidatesToReject })}
            </Body>
            <HelpTooltip
              label={t('reject.bulk.to_tooltip')}
              ariaLabel={t('reject.bulk.to_tooltip')}
            />
          </CompactRow>
        </Stack>
      )}
      <Stack spacing={1}>
        <PaginatedMultiDropdown
          getOptionsUrl={getCcOptionsPath}
          name="cc"
          label={t('email.cc')}
          creatable={true}
          disabled={templateLoading}
          isPiiBlurred
        />
        <CompactRow spacing={3}>
          <CheckboxField
            label={t('email.recruiter')}
            name="cc_recruiter"
            disabled={templateLoading}
            value="cc_recruiter"
          />
          <CheckboxField
            label={t('email.coordinator')}
            name="cc_coordinator"
            disabled={templateLoading}
            value="cc_coordinator"
          />
        </CompactRow>
      </Stack>
      <SimpleFormTextField
        id="subject"
        name={`subject`}
        label={t('email.subject')}
        rules={{ required: sendEmail && t('email.subject_required') }}
        disabled={templateLoading}
        inputClassName="pii-blur"
      />
      <Stack spacing={1}>
        <div className="pii-blur">
          <RichTextField
            compact
            name="html_body"
            useLegacyEditor
            rules={{ required: sendEmail && t('email.body_required') }}
            disabled={templateLoading}
          />
        </div>
        <CheckboxField
          label={t('email.include_self')}
          name="include_self"
          value="include_self"
        />
      </Stack>
      {attachmentOptions.length > 0 && (
        <SimpleFormMultiDropdown
          label={t('email.attachments')}
          name="default_attachments"
          items={attachmentOptions}
        />
      )}
      <SendLaterRow
        {...sendAt}
        dateFormatBirchPicker={dateFormatBirchPicker}
        shouldValidate={sendEmail}
      />
    </StackWithHiddenComponents>
  );
};

export default RejectionEmailFields;
